
import React, { createContext, useContext, useEffect, useState } from 'react';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import 'firebase/compat/firestore';
import { auth } from '../firebase';
import { userService } from '../services/userService';
import type { UserProfile } from '../types';

// Fallback for User type if it's missing from exports
type User = any;

interface AuthContextType {
    currentUser: User | null;
    userProfile: UserProfile | null;
    isAdmin: boolean;
    loading: boolean;
    signInWithGoogle: () => Promise<void>;
    signup: (email: string, password: string) => Promise<void>;
    login: (email: string, password: string) => Promise<void>;
    logout: () => Promise<void>;
    resetPassword: (email: string) => Promise<void>;
    refreshUserProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [currentUser, setCurrentUser] = useState<User | null>(null);
    const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
    const [loading, setLoading] = useState(true);

    // admin check based on email or profile (normalized isAdmin from userService)
    const isAdmin = (currentUser?.email?.toLowerCase().trim() === 'jcrrab1@gmail.com') || (!!userProfile?.isAdmin);

    const signInWithGoogle = async () => {
        const provider = new firebase.auth.GoogleAuthProvider();
        try {
            await auth.signInWithPopup(provider);
        } catch (error) {
            console.error("Error signing in with Google", error);
            throw error;
        }
    };

    const signup = async (email: string, password: string) => {
        try {
            const userCredential = await auth.createUserWithEmailAndPassword(email, password);
            if (userCredential.user) {
                // Initialize the user document in the "users" collection
                await firebase.firestore().collection('users').doc(userCredential.user.uid).set({
                    uid: userCredential.user.uid,
                    email: email,
                    createdAt: firebase.firestore.FieldValue.serverTimestamp()
                }, { merge: true });

                // Configure redirect back to the app after email verification
                const actionCodeSettings = {
                    url: window.location.origin, // Redirect to the app root
                    handleCodeInApp: false
                };
                await userCredential.user.sendEmailVerification(actionCodeSettings);
            }
        } catch (error) {
            console.error("Error signing up", error);
            throw error;
        }
    };

    const login = async (email: string, password: string) => {
        try {
            await auth.signInWithEmailAndPassword(email, password);
        } catch (error) {
            console.error("Error logging in", error);
            throw error;
        }
    };

    const logout = async () => {
        try {
            await auth.signOut();
            setUserProfile(null);
        } catch (error) {
            console.error("Error signing out", error);
        }
    };

    const resetPassword = async (email: string) => {
        try {
            await auth.sendPasswordResetEmail(email);
        } catch (error) {
            console.error("Error sending password reset email", error);
            throw error;
        }
    };

    const refreshUserProfile = async () => {
        if (auth.currentUser) {
            try {
                const profile = await userService.getUserProfile(auth.currentUser.uid);
                setUserProfile(profile);
            } catch (e) {
                console.error("Failed to refresh user profile", e);
            }
        }
    };

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged(async (user: any) => {
            if (user) {
                try {
                    const profile = await userService.getUserProfile(user.uid);
                    setUserProfile(profile);

                    // Manual Override: Check Firestore for 'emailVerified' boolean
                    if (profile && (profile as any).emailVerified === true) {
                        console.debug(`[Auth] Manual verification override applied for: ${user.email}`);
                        Object.defineProperty(user, 'emailVerified', { value: true, writable: true });
                    }
                } catch (e) {
                    console.error("Failed to fetch user profile in context", e);
                }
            } else {
                setUserProfile(null);
            }

            setCurrentUser(user);
            setLoading(false);
        });

        return unsubscribe;
    }, []);

    const value = {
        currentUser,
        userProfile,
        isAdmin,
        loading,
        signInWithGoogle,
        signup,
        login,
        logout,
        resetPassword,
        refreshUserProfile
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
};
